// Vendor Billing — the Stripe pay step of the portal Invoices tab. Mounts
// the official Payment Element (ACH Direct Debit + card tabs) against a
// server-created PaymentIntent: the backend owns the amount (always the
// full invoice amount) and the client secret; the browser only ever sees
// the publishable key. Confirmation never redirects for card/ACH
// (redirect: "if_required"); the settled state comes from the post-confirm
// server sync, not from trusting the client-side result alone.
//
// SP-4 WS-3 — generalized via callback props (createIntent/syncPayment) so
// the buyer service-fee surface reuses the SAME Stripe internals + honest
// ACH copy against its own borrower-scoped endpoints; the vendor tab passes
// portalVendorApi. Both flows charge the PHOENIX Stripe account.

import { useEffect, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  Button,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { type VendorInvoicePaymentIntentDto } from "../api/vendor";

export type PaymentSettledKind = "paid" | "processing";

// One Stripe.js load per publishable key for the whole session (the
// loader's own recommendation — loadStripe must not be called per render).
const stripeLoaders = new Map<string, Promise<Stripe | null>>();
function stripeFor(publishableKey: string): Promise<Stripe | null> {
  let loader = stripeLoaders.get(publishableKey);
  if (!loader) {
    loader = loadStripe(publishableKey);
    stripeLoaders.set(publishableKey, loader);
  }
  return loader;
}

interface InvoicePayPanelProps<TInvoice> {
  invoiceId: string;
  amountLabel: string;
  createIntent: (invoiceId: string) => Promise<VendorInvoicePaymentIntentDto>;
  syncPayment: (invoiceId: string) => Promise<TInvoice>;
  onSettled: (updated: TInvoice, kind: PaymentSettledKind) => void;
  onCancel: () => void;
}

export function InvoicePayPanel<TInvoice>({
  invoiceId,
  amountLabel,
  createIntent,
  syncPayment,
  onSettled,
  onCancel,
}: InvoicePayPanelProps<TInvoice>) {
  const [intent, setIntent] = useState<VendorInvoicePaymentIntentDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await createIntent(invoiceId);
        if (!cancelled) setIntent(data);
      } catch (err) {
        if (!cancelled)
          setError(
            err instanceof Error
              ? err.message
              : "Online payment could not be started. Please try again.",
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceId, createIntent]);

  if (error) {
    return (
      <div
        className="flex flex-col items-start gap-space-2"
        data-testid="invoice-pay-panel"
      >
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Back to invoice
        </Button>
      </div>
    );
  }
  if (!intent) {
    return (
      <p
        className="text-small text-gray-mid m-0"
        data-testid="invoice-pay-loading"
      >
        Preparing secure payment…
      </p>
    );
  }

  return (
    <div data-testid="invoice-pay-panel">
      <Elements
        stripe={stripeFor(intent.publishableKey)}
        options={{ clientSecret: intent.clientSecret }}
      >
        <ConfirmPaymentForm
          invoiceId={invoiceId}
          amountLabel={amountLabel}
          createIntent={createIntent}
          syncPayment={syncPayment}
          onSettled={onSettled}
          onCancel={onCancel}
        />
      </Elements>
    </div>
  );
}

function ConfirmPaymentForm<TInvoice>({
  invoiceId,
  amountLabel,
  syncPayment,
  onSettled,
  onCancel,
}: InvoicePayPanelProps<TInvoice>) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);

  const confirmPayment = useSubmittingMutation(
    async () => {
      if (!stripe || !elements) return;
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (result.error) {
        // The invoice stays payable — show the reason and let the vendor
        // retry with another method.
        setError(
          result.error.message
            ?? "The payment could not be completed. Please try again.",
        );
        return;
      }
      const status = result.paymentIntent?.status;
      if (status === "succeeded" || status === "processing") {
        const updated = await syncPayment(invoiceId);
        onSettled(updated, status === "succeeded" ? "paid" : "processing");
        return;
      }
      setError("The payment was not completed. Please try again.");
    },
    {
      onError: (err) =>
        setError(
          err instanceof Error
            ? err.message
            : "The payment could not be completed. Please try again.",
        ),
    },
  );
  const submitting = confirmPayment.pending;

  const confirm = () => {
    if (!stripe || !elements) return;
    setError(null);
    void confirmPayment.submit();
  };

  return (
    <form
      className="flex flex-col gap-space-3"
      onSubmit={(e) => {
        e.preventDefault();
        confirm();
      }}
    >
      <PaymentElement />
      <p className="text-caption text-gray-mid m-0">
        Bank transfers (ACH) typically take a few business days to settle;
        card payments confirm immediately. Phoenix Capital covers all
        processing fees.
      </p>
      {error && (
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center gap-space-2">
        <SubmitButton
          type="submit"
          variant="primary"
          pending={submitting}
          pendingLabel="Processing…"
          disabled={!stripe || !elements}
          data-testid="invoice-pay-confirm"
        >
          {`Pay ${amountLabel}`}
        </SubmitButton>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
      </div>
      <SubmittingHint pending={submitting} />
    </form>
  );
}
